// What a successful clear-unconfirmed-charge did, as the copy the page shows
// once the dialog closes (#439). Keyed on the resolution the admin sent: the
// API answers 200 with that same resolution, except on its idempotent no-op —
// the charge was no longer blocked — which it marks with a null cleared
// marker. That case must never read as a recorded payment.
import {
    HeldChargeResolution,
    type ClearActivationChargeResponse,
    type ClearRenewalChargeResponse,
    type ClearReturnedPaymentFeeChargeResponse,
} from "@models/billing";
import { currencyFormatter, formatUtcCalendarDate } from "@utils/formatter";

export interface ClearChargeSuccess {
    // false = the API's no-op: nothing was changed.
    changed: boolean;
    message: string;
}

// The API answered 200 but with a body these helpers can't read (a shape
// drift between deployments). The clear is committed, so it is still reported
// as done — just without the detail.
export const UNDESCRIBED_CLEAR: ClearChargeSuccess = {
    changed: true,
    message:
        "The clear went through. Check the list for the charge's current state.",
};

const noChange =(subject: string): ClearChargeSuccess => ({
    changed: false,
    message: `The charge for ${subject} was no longer blocked, so nothing was changed.`,
});

const voided = (subject: string): ClearChargeSuccess => ({
    changed: true,
    message: `Charge for ${subject} cleared as voided. No payment was recorded.`,
});

// A field the body does not carry at all is unreadable, never read as the
// no-op's null, as a void, or as a recorded payment (#469 round 1b, ruling 5).
export const renewalClearSuccess = (
    organizationName: string,
    resolution: HeldChargeResolution,
    response: ClearRenewalChargeResponse,
): ClearChargeSuccess => {
    if (response.clearedRenewalChargeUnconfirmedAt === undefined) {
        return UNDESCRIBED_CLEAR;
    }
    if (response.clearedRenewalChargeUnconfirmedAt === null) {
        return noChange(organizationName);
    }
    if (resolution === HeldChargeResolution.Voided) {
        return voided(organizationName);
    }
    // A recorded payment always carries its paid period.
    if (response.paidPeriodStart == null || response.paidPeriodEnd == null) {
        return UNDESCRIBED_CLEAR;
    }

    // The API's period end is exclusive (the next period starts on it), so
    // the last paid day is the day before — as on the customer's invoice.
    return {
        changed: true,
        message: `Payment recorded for ${organizationName}. The membership is paid from ${formatUtcCalendarDate(response.paidPeriodStart)} through ${formatUtcCalendarDate(response.paidPeriodEnd, { dayOffset: -1 })}.`,
    };
};

export const activationClearSuccess = (
    locationName: string,
    resolution: HeldChargeResolution,
    response: ClearActivationChargeResponse,
): ClearChargeSuccess => {
    if (response.clearedActivationChargeUnconfirmedAt === undefined) {
        return UNDESCRIBED_CLEAR;
    }
    if (response.clearedActivationChargeUnconfirmedAt === null) {
        return noChange(locationName);
    }
    if (resolution === HeldChargeResolution.Voided) {
        return voided(locationName);
    }

    // A recorded activation charge activates the location and completes a
    // pending-payment approval.
    return {
        changed: true,
        message: `Payment recorded. ${locationName} is approved and active.`,
    };
};

// #469. Released and Settled record the transaction as the order's fee, at the
// amount the API answers (the vendor's settled amount on Settled). A field the
// body does not carry at all is unreadable, never read as the no-op's null or
// as a void (round 1, finding 4).
export const returnedPaymentFeeClearSuccess = (
    orderNumberFormatted: string,
    resolution: HeldChargeResolution,
    response: ClearReturnedPaymentFeeChargeResponse,
): ClearChargeSuccess => {
    const order = `order ${orderNumberFormatted}`;

    if (response.clearedReturnedPaymentFeeUnconfirmedAt === undefined) {
        return UNDESCRIBED_CLEAR;
    }
    if (response.clearedReturnedPaymentFeeUnconfirmedAt === null) {
        return {
            changed: false,
            message: `The fee charge for ${order} was no longer unconfirmed, so nothing was changed.`,
        };
    }
    if (resolution === HeldChargeResolution.Voided) {
        return {
            changed: true,
            message: `Fee charge for ${order} cleared as voided. No fee was recorded.`,
        };
    }

    const amount = response.returnedPaymentFeeAmount;
    const transactionId = response.returnedPaymentFeeTransactionId;
    if (amount == null || transactionId == null) {
        return UNDESCRIBED_CLEAR;
    }

    return {
        changed: true,
        message: `Returned-payment fee of ${currencyFormatter.format(amount)} recorded for ${order} (transaction ${transactionId}).`,
    };
};
